import type { ChessConstructor } from "../chess/chessTypes.ts";
import { safeMove } from "../chess/notation.ts";
import { positionKey, START_FEN } from "../chess/positionKey.ts";
import type { ConceptTag } from "./ids.ts";
import type { LessonCourse, LessonMove, LessonNode, LessonPhase, MoveRole } from "./schema.ts";
import { validateCourse, type GraphIssue } from "./validator.ts";

export type AuthoredMove = {
  san: string;
  role: MoveRole;
  explanation: string;
  concepts?: ConceptTag[];
  variationName?: string;
  objective?: string;
  opponentIntent?: string;
  handoff?: boolean;
  phase?: LessonPhase;
  tacticalWarning?: boolean;
  tradeoff?: string;
  eco?: string;
  hintSteps?: string[];
  piece?: string;
  candidates?: string;
  next?: string;
  watch?: string;
};

export type AuthoredLine = {
  after: string[];
  move: AuthoredMove;
  /** The line sits inside a PGN variation rather than on the main path. */
  branch?: boolean;
};

export type CourseSpec = {
  id: string;
  openingId: LessonCourse["openingId"];
  slug: string;
  title: string;
  side: "white" | "black";
  targetFullMoves: number;
  level: "beginner" | "intermediate";
  objectives: string[];
  contentVersion: number;
  rootFen?: string;
  rootObjective: string;
  rootExplanation: string;
  lines: AuthoredLine[];
  reviewStatus?: LessonCourse["reviewStatus"];
  limitedCoverage?: boolean;
  branchPolicy?: LessonCourse["branchPolicy"];
};

function ensureNode(
  nodes: Record<string, LessonNode>,
  fen: string,
  ply: number,
  defaults: { objective: string; explanation: string; phase: LessonPhase }
): LessonNode {
  const key = positionKey(fen);
  const existing = nodes[key];
  if (existing) return existing;
  const node: LessonNode = {
    positionKey: key,
    fen,
    ply,
    phase: defaults.phase,
    objective: defaults.objective,
    explanation: defaults.explanation,
    concepts: [],
    moves: [],
    handoff: false,
  };
  nodes[key] = node;
  return node;
}

function phaseForPly(ply: number): LessonPhase {
  if (ply <= 4) return "center";
  if (ply <= 8) return "development";
  if (ply <= 12) return "kingSafety";
  return "plan";
}

export function buildCourse(Chess: ChessConstructor, spec: CourseSpec): LessonCourse {
  const rootFen = spec.rootFen || START_FEN;
  const nodes: Record<string, LessonNode> = {};
  const root = ensureNode(nodes, rootFen, 0, {
    objective: spec.rootObjective,
    explanation: spec.rootExplanation,
    phase: "center",
  });

  // Many lines share long move prefixes, so remember the position each prefix
  // reaches instead of replaying every line from the start.
  const reached = new Map<string, string>();
  const reach = (after: string[]) => {
    let known = after.length;
    while (known > 0 && !reached.has(after.slice(0, known).join(" "))) known -= 1;
    const from = known > 0 ? reached.get(after.slice(0, known).join(" ")) : spec.rootFen;
    const chess = from ? new Chess(from) : new Chess();
    for (let i = known; i < after.length; i += 1) {
      if (!safeMove(chess, after[i])) {
        throw new Error(`${spec.id}: cannot reach ${after.join(" ")}`);
      }
      reached.set(after.slice(0, i + 1).join(" "), chess.fen());
    }
    return chess;
  };

  // Which line first wrote a node's text. A variation that transposes into
  // the main line is listed before the main line reaches the same node; the
  // node must still read as the main line's, not as "Black transposes".
  const textSource = new Map<string, number>();
  const textRank = (line: AuthoredLine) =>
    (line.branch ? 0 : 10) +
    (line.move.role === "mainline" || line.move.role === "repertoire"
      ? 2
      : line.move.role === "sideline"
        ? 1
        : 0);

  for (const line of spec.lines) {
    const chess = reach(line.after);
    const fromFen = chess.fen();
    const fromPly = line.after.length;
    const fromNode = ensureNode(nodes, fromFen, fromPly, {
      objective: spec.rootObjective,
      explanation: spec.rootExplanation,
      phase: phaseForPly(fromPly),
    });
    if (line.move.objective) {
      fromNode.objective = line.move.objective;
      fromNode.objectiveAuthored = true;
    }
    const played = safeMove(chess, line.move.san);
    if (!played) {
      throw new Error(`${spec.id}: illegal ${line.move.san} after ${line.after.join(" ")}`);
    }
    const nextFen = chess.fen();
    const nextPly = fromPly + 1;
    const existed = Boolean(nodes[positionKey(nextFen)]);
    const nextNode = ensureNode(nodes, nextFen, nextPly, {
      objective: spec.rootObjective,
      explanation: line.move.explanation,
      phase: line.move.phase || phaseForPly(nextPly),
    });
    if (!existed) {
      textSource.set(nextNode.positionKey, textRank(line));
    } else if (textRank(line) > (textSource.get(nextNode.positionKey) ?? 0)) {
      // A main-path move reaching a node first written by a variation gives
      // the node its own text; the variation's name and intent go with it.
      nextNode.explanation = line.move.explanation;
      delete nextNode.variationName;
      delete nextNode.opponentIntent;
      textSource.set(nextNode.positionKey, textRank(line));
    }
    if (line.move.variationName && !nextNode.variationName) {
      nextNode.variationName = line.move.variationName;
    }
    if (line.move.opponentIntent && !nextNode.opponentIntent) {
      nextNode.opponentIntent = line.move.opponentIntent;
    }
    if (line.move.handoff) nextNode.handoff = true;
    if (line.move.eco && !nextNode.eco) nextNode.eco = line.move.eco;

    const uci = played.from + played.to + (played.promotion || "");
    const already = fromNode.moves.find((m) => m.uci === uci);
    if (!already) {
      const edge: LessonMove = {
        uci,
        san: played.san,
        nextPositionKey: nextNode.positionKey,
        role: line.move.role,
        explanation: line.move.explanation,
        concepts: line.move.concepts || [],
        hintSteps: line.move.hintSteps || [],
        ...(line.move.tradeoff ? { tradeoff: line.move.tradeoff } : {}),
        ...(line.move.tacticalWarning ? { tacticalWarning: true } : {}),
        ...(line.move.variationName ? { variationName: line.move.variationName } : {}),
        ...(line.move.piece ? { piece: line.move.piece } : {}),
        ...(line.move.candidates ? { candidates: line.move.candidates } : {}),
        ...(line.move.next ? { next: line.move.next } : {}),
        ...(line.move.watch ? { watch: line.move.watch } : {}),
      };
      fromNode.moves.push(edge);
    }
    if (line.move.role === "repertoire" || line.move.role === "mainline") {
      fromNode.concepts = [...new Set([...fromNode.concepts, ...(line.move.concepts || [])])];
    }
  }

  const course: LessonCourse = {
    id: spec.id,
    openingId: spec.openingId,
    slug: spec.slug,
    title: spec.title,
    side: spec.side,
    rootPositionKey: root.positionKey,
    targetFullMoves: spec.targetFullMoves,
    level: spec.level,
    objectives: spec.objectives,
    contentVersion: spec.contentVersion,
    nodes,
    reviewStatus: spec.reviewStatus || "draft",
    limitedCoverage: Boolean(spec.limitedCoverage),
    ...(spec.branchPolicy ? { branchPolicy: spec.branchPolicy } : {}),
  };
  return course;
}

export type LessonLookup = {
  inGraph: boolean;
  pastSlice: boolean;
  node: LessonNode | null;
  edge: LessonMove | null;
  repertoireMove: LessonMove | null;
};

/** The shortest sequence of course moves from the root to a position, or null. */
export function pathToPosition(course: LessonCourse, positionKey: string): string[] | null {
  const seen = new Set([course.rootPositionKey]);
  const queue: Array<{ key: string; path: string[] }> = [{ key: course.rootPositionKey, path: [] }];
  while (queue.length) {
    const { key, path } = queue.shift()!;
    if (key === positionKey) return path;
    for (const move of course.nodes[key]?.moves || []) {
      if (seen.has(move.nextPositionKey)) continue;
      seen.add(move.nextPositionKey);
      queue.push({ key: move.nextPositionKey, path: [...path, move.san] });
    }
  }
  return null;
}

/** The node's objective only when it was written for this position. */
export function authoredObjective(node: LessonNode | null | undefined): string | null {
  return node?.objectiveAuthored ? node.objective : null;
}

// Some move-order branches return to the course through a transposition edge.
export function teachingMoveFor(node: LessonNode | null | undefined): LessonMove | null {
  return (
    node?.moves.find((move) => move.role === "repertoire" || move.role === "mainline") ||
    node?.moves.find((move) => move.role === "transposition") ||
    null
  );
}

export function lookupLesson(
  course: LessonCourse,
  fen: string,
  played: { san?: string; uci?: string }
): LessonLookup {
  const key = positionKey(fen);
  const node = course.nodes[key] || null;
  if (!node) {
    return { inGraph: false, pastSlice: false, node: null, edge: null, repertoireMove: null };
  }
  const edge =
    node.moves.find(
      (m) => (played.uci && m.uci === played.uci) || (played.san && m.san === played.san)
    ) || null;
  const repertoireMove = teachingMoveFor(node);
  return {
    inGraph: true,
    pastSlice: Boolean(node.handoff),
    node,
    edge,
    repertoireMove,
  };
}

export function assertValidCourse(course: LessonCourse, Chess: ChessConstructor): void {
  const issues: GraphIssue[] = validateCourse(course, Chess);
  if (issues.length) {
    throw new Error(issues.map((i) => `${i.code}: ${i.message}`).join("\n"));
  }
}
